import type { User } from 'firebase/auth';
import { useMemo } from 'react';
import { type DiscoveryFilter } from '@/services';
import { filterDefaults, useFilterStore, type PreferencesSnapshot } from '@/stores/filterStore';
import { UserProfile } from '@/types';
import { useBlocks } from './useBlocks';
import { useViewerLocation } from './useViewerLocation';

export function useFilters(user: User | null, profile: UserProfile | null) {
  const { location, locationEnabled } = useViewerLocation();
  const {
    filterMinAge,
    filterMaxAge,
    filterDistance,
    availableToday,
    favoritesOnly,
    intentions,
    openTo,
    relationshipStatus,
    genderMode,
    ethnicityMode,
  } = useFilterStore();
  const { blockedUids, blockedByUids } = useBlocks(user);
  const hasSubscription = profile?.subscription?.active ?? false;

  return useMemo<DiscoveryFilter>(() => {
    let mineEthnicity: string[] = [];
    if (profile?.ethnicity) {
      mineEthnicity = Array.isArray(profile.ethnicity) ? profile.ethnicity : [profile.ethnicity];
    }

    const currentLatitude = location.latitude;
    const currentLongitude = location.longitude;
    const hasCurrentLocation =
      typeof currentLatitude === 'number' && typeof currentLongitude === 'number';
    const canFilterByDistance = locationEnabled && hasCurrentLocation;

    // Freemium: filters are subscriber-only — non-subscribers always query
    // with store defaults so stale stored values can't constrain their feed.
    const prefs: PreferencesSnapshot = hasSubscription
      ? {
          filterMinAge,
          filterMaxAge,
          filterDistance,
          availableToday,
          intentions,
          openTo,
          relationshipStatus,
          genderMode,
          ethnicityMode,
        }
      : filterDefaults;

    return {
      ageRange: [prefs.filterMinAge, prefs.filterMaxAge],
      availableToday: (profile?.availableToday ?? false) && prefs.availableToday,
      ...(prefs.intentions.length > 0 ? { intentions: prefs.intentions } : {}),
      openTo: prefs.openTo,
      relationshipStatus: prefs.relationshipStatus.map(id => id.toLowerCase().replace(/\s+/g, '')),
      genderMode: prefs.genderMode,
      ethnicityMode: prefs.ethnicityMode,
      gender: profile?.gender,
      ethnicity: mineEthnicity,
      blockedUids,
      blockedByUids,
      favoritesOnly,
      ...(canFilterByDistance
        ? { distance: prefs.filterDistance, currentLatitude, currentLongitude }
        : {}),
    };
  }, [
    filterMinAge,
    filterMaxAge,
    filterDistance,
    availableToday,
    intentions,
    openTo,
    relationshipStatus,
    genderMode,
    ethnicityMode,
    profile,
    location,
    locationEnabled,
    hasSubscription,
    blockedUids,
    blockedByUids,
    favoritesOnly,
  ]);
}
